import { css, type Theme } from "@emotion/react";

import { animations, dimensions, layers, timing, utilities } from "@styles";
import { autoFillDetector } from "@styles/helpers";

export const controlBase = (theme: Theme) => css`
  transition: all ${timing.moderate} ${animations.easeOutCubic};
  color: ${theme.input.textBase};
  align-items: center;
  display: inline-flex;
  font-size: 1rem;
  height: auto;
  justify-content: flex-start;
  line-height: 1.5;
  position: relative;
  vertical-align: top;
  width: 100%;
  background-color: ${theme.input.backgroundBase};

  box-sizing: border-box;
  clear: both;
  text-align: left;

  /* States */
  &:focus,
  &:active {
    outline: none;
  }

  &[disabled],
  fieldset[disabled] & {
    cursor: not-allowed;
  }
`;

export const floatingLabelInputErrorWrapper = css`
  width: 100%;
`;

export const floatingLabelWrapper = (theme: Theme) => css`
  ${controlBase(theme)};
  z-index: 1;
  border-radius: ${dimensions.borderRadius} ${dimensions.borderRadius} 0 0;
`;

export const requiredAsterisk = (theme: Theme) => css`
  color: ${theme.input.textFloatingLabelError};
`;

export const htmlInput = {
  base: (theme: Theme) => css`
    ${controlBase(theme)};
    ${autoFillDetector};
    z-index: 2;
    top: 0;
    appearance: none;
    border: 1px solid ${theme.input.borderBase};
    border-bottom: 1px solid ${theme.input.borderEmphasized};
    border-radius: ${dimensions.borderRadius} ${dimensions.borderRadius} 0 0;
    box-shadow: none;
    padding: 1rem;

    &:hover {
      background-color: ${theme.input.backgroundHover};
    }

    &:focus {
      border-bottom-color: ${theme.input.borderActive};
    }

    &[disabled],
    fieldset[disabled] & {
      background-color: ${theme.disabled.inputBackground};
      color: ${theme.disabled.text};
      border-color: ${theme.disabled.border};
      border-bottom-color: ${theme.input.borderEmphasized};
    }

    &::placeholder {
      color: ${theme.input.textPlaceholder};
    }
  `,
  filled: (theme: Theme) => css`
    border-bottom-color: ${theme.input.borderEmphasized};
  `,
  filledWithLabel: css`
    padding-top: 1.5rem;
    padding-bottom: 0.5rem;
  `,
  icon: css`
    padding-right: 2.5rem;
  `,
  errorStyles: (theme: Theme) => css`
    border-color: ${theme.input.borderError};
    border-bottom-color: ${theme.input.borderError};

    &:hover {
      border-color: ${theme.input.borderError};
      border-bottom-color: ${theme.input.borderError};
    }
    &:focus {
      border-bottom-color: ${theme.input.borderError};
    }
  `,
  hasWhiteBackground: (theme: Theme) => css`
    background-color: ${theme.input.backgroundInformation};

    &:hover {
      background-color: ${theme.input.backgroundInformation};

      &[disabled] {
        background-color: ${theme.disabled.inputBackground};
      }
    }
  `,
};

export const label = {
  base: (theme: Theme) => css`
    transition:
      transform 0.1s cubic-bezier(0.1, 0.55, 0.83, 0.67),
      color 0.3s ease-out;
    font-size: 1rem;
    position: absolute;
    transform-origin: top left;
    transform: translate(16px, 0) scale(1);
    color: ${theme.input.textLabel};
    cursor: text;
    z-index: 3;
  `,
  hidden: css`
    ${utilities.visuallyHidden};
  `,
  focused: (theme: Theme) => css`
    color: ${theme.input.textFloatingLabel};
    transform: translate(16px, -12px) scale(0.85);
  `,
  filled: css`
    transform: translate(16px, -12px) scale(0.85);
  `,
  disabled: (theme: Theme) => css`
    color: ${theme.disabled.inputLabelText};
    cursor: not-allowed;
  `,
  hasIcon: css`
    padding-right: 2.5rem;
  `,
  errorStyles: (theme: Theme) => css`
    color: ${theme.input.textFloatingLabelError};
  `,
};

export const icon = {
  base: css`
    position: absolute;
    right: 0;
    top: calc(50%);
    transform: translate(-50%, -50%);
    padding: 0.325rem;
    z-index: ${layers.zIndexLowForeground};
  `,

  disabled: (theme: Theme) => css`
    color: ${theme.disabled.inputLabelText};
  `,
};
